import {
  Box,
  Checkbox,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import {
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../../components/datagrid/AppDataGrid";
import { tableStyle } from "../../../../emission/_components/data-table/EmissionDataTableStyle";
import { common, economic, input } from "../../../../../i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomSelect2 } from "../../../../../components/input/CustomSelect2";
import { EcoEfficiency } from "../../../../../models/dto/response/ResponseEcoEfficiency";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { EcoEfficiencyContext } from "../../../../../contexts/EcoEfficiencyContext";
import { POSITIVE_NUMBER_REGEX } from "../../../../../constants/form";
import { NumericFormat } from "react-number-format";

interface Props {
  editData: EcoEfficiency | undefined;
  percentChangesForm: UseFormReturn;
  rawDataForm: UseFormReturn;
  fiveYearForecast: number[];
}

export const textFieldEndAlignWithFloatingHelper: SxProps<Theme> = {
  ".MuiOutlinedInput-input": {
    textAlign: "end",
  },
  ".MuiFormHelperText-root": {
    position: "absolute",
    bottom: "-22px",
  },
};

export const PlanSettingPercentChangesTable = ({
  editData,
  percentChangesForm,
  fiveYearForecast,
  rawDataForm,
}: Props) => {
  const { t, i18n } = useTranslation([common, input, economic], {
    nsMode: "fallback",
  });

  // 🧠 ดูทุกฟิลด์ใน form
  const watchedValues = useWatch({
    control: percentChangesForm.control,
  });

  useEffect(() => {
    if (rows.length > 0) {
      setRows([...rows]);
    }
  }, [watchedValues]);

  const { calEcoEfficiencyPlans } = EcoEfficiencyContext();

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Planned-Year"),
      children: [{ field: `${fiveYearForecast[0]}` }],
      headerAlign: "center",
    },
    {
      groupId: t("Five-Year-Forecast"),
      children: [
        ...fiveYearForecast
          .slice(1, fiveYearForecast.length)
          .map((year, index) => ({
            field: `${year}`,
          })),
      ],
      headerAlign: "center",
    },
  ];

  const columns: GridColDef<EconomicIndicatorComponent>[] = [
    {
      field: "id",
      headerName: t("ID"),
      width: 80,
      sortable: false,
      filterable: false,
      colSpan: (params, row) => (row._id === "TOTAL" ? 2 : 1),
      renderCell: (params) => {
        return params.row._id !== "TOTAL"
          ? `EI-${params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}`
          : `${t("Emission")} (kgCO₂e)`;
      },
    },
    {
      field: "nameEn",
      headerName: t("Components"),
      headerAlign: "left",
      minWidth: 140,
      flex: 1,
      sortable: false,
      valueGetter: (value, row) =>
        i18n.language === "th" ? row.nameTh : row.nameEn,
    },
    {
      field: "rateType",
      headerName: t("Rate-Type"),
      headerAlign: "center",
      align: "left",
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const activated = percentChangesForm.getValues(
          `${params.row._id === "TOTAL" ? "TOTAL" : params.row._id}-activated`,
        );

        // console.log(
        //   percentChangesForm.getValues(`${params.row._id}-rate-Type`)
        // );

        return params.row._id !== "TOTAL" ? (
          <CustomSelect2
            disabled={!activated}
            name={`${params.row._id}-rate-Type`}
            control={percentChangesForm.control}
            label={""}
            info={""}
            placeholder={""}
            listMenuItem={[
              {
                nameEN: t("Increase"),
                nameTH: t("Increase"),
                value: "increase",
              },
              {
                nameEN: t("Decrease"),
                nameTH: t("Decrease"),
                value: "decrease",
              },
            ]}
            showLabelInput={false}
            defaultValue={"increase"}
            rules={{
              onChange(event) {
                calEcoEfficiencyPlans(rows, fiveYearForecast);
              },
            }}
          />
        ) : (
          <CustomSelect2
            name={`total-rate-Type`}
            control={percentChangesForm.control}
            label={""}
            info={""}
            placeholder={""}
            listMenuItem={[
              {
                nameEN: t("Decrease"),
                nameTH: t("Decrease"),
                value: "decrease",
              },
            ]}
            showLabelInput={false}
            defaultValue={"decrease"}
            disabled
          />
        );
      },
    },
    {
      field: "activated",
      headerName: t("Activated"),
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const key = `${params.id}-activated`;

        return (
          <>
            <Controller
              name={key}
              control={percentChangesForm.control}
              defaultValue={true}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  sx={{ margin: "0 auto", display: "block" }} // จัดให้อยู่ตรงกลาง
                />
              )}
            />
            {/* <Controller
              name={key}
              control={percentChangesForm.control}
              defaultValue={true}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      sx={{ margin: "0 auto", display: "block" }} // จัดให้อยู่ตรงกลาง
                    />
                  }
                  label={``}
                  checked
                /> 
              )}
            /> */}
          </>
        );
      },
    },
    ...fiveYearForecast.map((y) => {
      return {
        field: `${y}`,
        headerName: `${y}`,
        headerAlign: "center",
        align: "left",
        disableColumnMenu: true,
        minWidth: 140,
        editable: false,
        sortable: false,
        renderCell: (params) => {
          const key = `${
            params.row._id === "TOTAL" ? "total" : params.row._id
          }-${y}-forecast`;

          const activated = percentChangesForm.getValues(
            `${
              params.row._id === "TOTAL" ? "TOTAL" : params.row._id
            }-activated`,
          );

          const disabled =
            activated === undefined || activated === true ? false : true;

          return (
            <Box
              sx={{
                ...textFieldEndAlignWithFloatingHelper,
              }}
            >
              <Controller
                name={key}
                key={key}
                control={percentChangesForm.control}
                rules={{
                  max: {
                    value: 100,
                    message: t("Maximum-100"),
                  },
                  min: {
                    value: 0,
                    message: t("Positive-Only"),
                  },
                  pattern: {
                    value: POSITIVE_NUMBER_REGEX,
                    message: t("Numbers-Only"),
                  },
                }}
                render={({ field }) => (
                  <NumericFormat
                    name={key}
                    key={key}
                    customInput={TextField}
                    thousandSeparator=","
                    allowNegative={false}
                    allowLeadingZeros={false}
                    fixedDecimalScale
                    value={field.value ?? ""}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue ?? null);
                    }}
                    onBlur={() => {
                      const value = percentChangesForm.getValues(key);
                      if (value !== "" && value !== undefined) {
                        calEcoEfficiencyPlans(rows, fiveYearForecast);
                      }
                    }}
                    placeholder="0.00"
                    fullWidth
                    disabled={disabled}
                    onKeyDown={(event) => {
                      event.stopPropagation();
                    }}
                    onKeyUp={async (event: React.KeyboardEvent) => {
                      switch (event.key) {
                        case "Enter":
                          (document.activeElement as HTMLElement).blur();
                          break;
                        default:
                          break;
                      }
                    }}
                    error={percentChangesForm.formState.errors[key] && true}
                    helperText={
                      disabled
                        ? undefined
                        : percentChangesForm.formState.errors[key] &&
                          percentChangesForm.formState.errors[
                            key
                          ]?.message?.toString()
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{
                            pointerEvents: "none",
                          }}
                        >
                          %
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          );
        },
      } as GridColDef<EconomicIndicatorComponent>;
    }),
  ];

  const [rows, setRows] = useState<EconomicIndicatorComponent[]>([]);

  useEffect(() => {
    if (editData) {
      setRows([
        ...editData.economicindicatorsRef.components,
        { _id: "TOTAL", nameEn: "total", nameTh: "total", unit: "total" },
      ]);
    }
  }, [editData, fiveYearForecast]);

  useEffect(() => {
    if (!rows.length) return;

    const cur = percentChangesForm.getValues();
    const patch: Record<string, any> = {};

    for (const r of rows) {
      if (r._id === "TOTAL") {
        if (cur["total-rate-Type"] === undefined)
          patch["total-rate-Type"] = "decrease";
        if (cur["TOTAL-activated"] === undefined)
          patch["TOTAL-activated"] = true;
        continue;
      }

      const rateKey = `${r._id}-rate-Type`;
      const actKey = `${r._id}-activated`;

      if (cur[rateKey] === "") patch[rateKey] = "increase";
      if (cur[actKey] === undefined) patch[actKey] = true;
    }

    if (Object.keys(patch).length) {
      // รวมค่าปัจจุบัน + ค่าที่ต้องเติม แล้ว reset เข้าไป
      percentChangesForm.reset(
        { ...cur, ...patch },
        {
          keepDirty: false, // ถ้าไม่อยากให้กลายเป็น dirty ทั้งหมดก็เปลี่ยนเป็น false
          keepTouched: false,
        },
      );
    }
  }, [rows, percentChangesForm]);

  return (
    <Box width={"100%"}>
      <AppDataGrid
        rows={rows}
        columnGroupingModel={columnGroup}
        columns={columns}
        rowHeight={90}
        sx={{ ...tableStyle("light") }}
        getRowId={(row) => row._id}
        disableMultipleRowSelection={true}
        hideFooter
        autoHeight
      />
    </Box>
  );
};
